import { useRef, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { X, MagnifyingGlass } from "@phosphor-icons/react";

const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || "").trim());
const ROLES = [["to", "An"], ["cc", "Cc"], ["bcc", "Bcc"]];

function AddressInput({ role, label, list, add, remove, disabled }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  const t = useRef(null);
  const search = (v) => {
    setQ(v);
    clearTimeout(t.current);
    if (v.trim().length < 2) { setRes([]); return; }
    t.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/contacts?q=${encodeURIComponent(v)}&limit=8`);
        const out = [];
        for (const c of (Array.isArray(data) ? data : data?.items || [])) {
          for (const e of c.emails || []) {
            const email = typeof e === "string" ? e : e?.email;
            if (email) out.push({ email, label: c.display_name || c.company_name || `${c.first_name || ""} ${c.last_name || ""}`.trim() });
          }
        }
        setRes(out.slice(0, 10));
      } catch { setRes([]); }
    }, 200);
  };
  const take = (v) => { if (isValidEmail(v)) { add(v.trim()); setQ(""); setRes([]); } };
  return (
    <div className="flex items-start gap-2" data-testid={`send-email-field-${role}`}>
      <span className="w-8 pt-1.5 text-[10px] uppercase font-semibold text-slate-500">{label}</span>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap gap-1">
          {list.map((em) => (
            <span key={em} className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-900 border border-emerald-200 inline-flex items-center gap-1 max-w-full break-all" data-testid={`send-${role}-chip-${em}`}>
              {em}<button type="button" onClick={() => remove(em)} className="hover:text-rose-600" title="Entfernen" data-testid={`send-${role}-remove-${em}`}><X size={9} /></button>
            </span>))}
        </div>
        <div className="relative mt-1">
          <MagnifyingGlass size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} disabled={disabled} onChange={(e) => search(e.target.value)} onBlur={() => isValidEmail(q) && take(q)}
                 onKeyDown={(e) => { if ((e.key === "Enter" || e.key === ",") && q.trim()) { e.preventDefault(); take(q); } else if (e.key === "Backspace" && !q && list.length) remove(list[list.length - 1]); }}
                 placeholder="Kontakt suchen oder E-Mail eingeben (Enter)" className="h-7 text-xs pl-6" data-testid={`send-${role}-input`} />
        </div>
        {res.length > 0 && (
          <div className="mt-1 border border-slate-200 rounded bg-white shadow-sm max-h-40 overflow-y-auto">
            {res.map((r, i) => (
              <button key={r.email + i} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => take(r.email)} className="w-full text-left px-2 py-1 text-[11px] hover:bg-emerald-50 border-b last:border-b-0 border-slate-100" data-testid={`send-${role}-result-${i}`}>
                <div className="font-medium text-slate-900">{r.label}</div><div className="text-[10px] text-slate-500">{r.email}</div>
              </button>))}
          </div>)}
      </div>
    </div>
  );
}

/** An / Cc / Bcc: bekannte Adressen per Klick zuordnen, weitere per Suche oder Freitext. */
export function EmailAddressFields({ available = [], to, setTo, cc, setCc, bcc, setBcc, disabled }) {
  const lists = { to, cc, bcc };
  const setters = { to: setTo, cc: setCc, bcc: setBcc };
  const roleOf = (em) => ROLES.find(([r]) => lists[r].includes(em))?.[0];
  const assign = (em, role) => {
    const cur = roleOf(em);
    ROLES.forEach(([r]) => { if (lists[r].includes(em)) setters[r](lists[r].filter((x) => x !== em)); });
    if (cur !== role) setters[role]([...lists[role].filter((x) => x !== em), em]);
  };
  const add = (role) => (em) => { if (roleOf(em)) assign(em, role); else setters[role]([...lists[role], em]); };
  const remove = (role) => (em) => setters[role](lists[role].filter((x) => x !== em));
  return (
    <div className="mt-2 space-y-2" data-testid="send-email-fields">
      {available.length > 0 && (
        <div className="space-y-1" data-testid="send-email-to-picker">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Bekannte Adressen</div>
          {available.map((em, i) => {
            const email = typeof em === "string" ? em : em.email;
            const r = roleOf(email);
            return (
              <div key={email + i} className="flex items-center gap-1.5 text-[11px]" data-testid={`send-email-known-${i}`}>
                <div className="inline-flex rounded border border-slate-200 overflow-hidden shrink-0">
                  {ROLES.map(([role, lbl]) => (
                    <button key={role} type="button" disabled={disabled} onClick={() => assign(email, role)} data-testid={role === "to" ? `send-email-to-${i}` : `send-email-${role}-${i}`}
                            className={`px-1.5 py-0.5 text-[10px] ${r === role ? "bg-emerald-600 text-white" : "bg-white text-slate-600 hover:bg-emerald-50"}`}>{lbl}</button>))}
                </div>
                <span className="truncate" title={email}>{em.is_primary ? "★ " : ""}{em.label ? <b className="font-medium">{em.label}: </b> : null}{email}</span>
              </div>);
          })}
        </div>)}
      {ROLES.map(([role, lbl]) => <AddressInput key={role} role={role} label={lbl} list={lists[role]} add={add(role)} remove={remove(role)} disabled={disabled} />)}
    </div>
  );
}
